import React, { useEffect } from "react";
import { X, Inbox } from "lucide-react";

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5 fade-up">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

const TONES: Record<string, string> = {
  blue: "text-blue-700 bg-blue-50",
  green: "text-emerald-700 bg-emerald-50",
  red: "text-red-600 bg-red-50",
  slate: "text-slate-700 bg-slate-100",
  amber: "text-amber-700 bg-amber-50",
};

export function Stat({ label, value, icon: Icon, tone = "blue", testid, hint }: any) {
  return (
    <div className="card p-4 md:p-5 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        {Icon && (
          <span className={`w-9 h-9 rounded-lg grid place-items-center ${TONES[tone]}`}>
            <Icon size={18} />
          </span>
        )}
      </div>
      <p className={`num mt-2 text-xl md:text-2xl font-bold ${tone === "red" ? "text-red-600" : tone === "green" ? "text-emerald-700" : "text-slate-900"}`} data-testid={testid ? `${testid}-value` : undefined}>
        {value}
      </p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

export function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export function Modal({ open, onClose, title, children, wide, testid }: any) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4 no-print" onMouseDown={onClose}>
      <div
        className={`bg-white w-full ${wide ? "sm:max-w-4xl" : "sm:max-w-lg"} rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col fade-up`}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button className="w-9 h-9 grid place-items-center rounded-lg hover:bg-slate-100" onClick={onClose} data-testid="modal-close-button" aria-label="বন্ধ">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export function Empty({ text = "কোনো তথ্য নেই" }: { text?: string }) {
  return (
    <div className="py-10 text-center text-slate-400" data-testid="empty-state">
      <Inbox className="mx-auto mb-2" size={28} />
      <p className="text-sm">{text}</p>
    </div>
  );
}

export function Badge({ tone = "slate", children }: { tone?: string; children: React.ReactNode }) {
  return <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${TONES[tone]}`}>{children}</span>;
}

export function Loader() {
  return (
    <div className="py-16 grid place-items-center" data-testid="loader">
      <div className="w-8 h-8 rounded-full border-[3px] border-blue-200 border-t-blue-700 animate-spin" />
    </div>
  );
}

export function GlassPage({ children, testid }: { children: React.ReactNode; testid?: string }) {
  return (
    <div
      data-testid={testid}
      className="relative -m-4 md:-m-6 lg:-m-8 p-4 md:p-6 lg:p-8 min-h-[calc(100vh-4rem)] overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 15% -5%, #163a6e 0%, transparent 55%), radial-gradient(900px 500px at 100% 0%, #0d5c3a 0%, transparent 50%), linear-gradient(160deg, #0a1326 0%, #0b1a33 60%, #0a1326 100%)",
      }}
    >
      {children}
    </div>
  );
}

export function GlassHeader({ title, subtitle, actions, testid }: { title: string; subtitle?: string; actions?: React.ReactNode; testid?: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5 fade-up">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid={testid || "page-title"}>{title}</h1>
        {subtitle && <p className="text-sm text-slate-300/90 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
